import { useEffect } from "react";
import { riderApi } from "@/features/delivery/api";

/**
 * useLiveGpsPusher — pushes the rider's coordinates to the backend every
 * `intervalMs` (default 5s) while enabled. Uses `navigator.geolocation`
 * with a soft failure path (silent when denied — rider can still work
 * without GPS, admin will just see stale coords).
 */
export function useLiveGpsPusher(enabled: boolean, intervalMs = 5000): void {
    useEffect(() => {
        if (!enabled) return;
        if (typeof navigator === "undefined" || !navigator.geolocation) return;

        let cancelled = false;

        const push = () => {
            navigator.geolocation.getCurrentPosition(
                (p) => {
                    if (cancelled) return;
                    riderApi
                        .toggleOnline(true, { lat: p.coords.latitude, lng: p.coords.longitude })
                        .catch(() => {
                            /* silent — a single failure is fine, we retry next tick */
                        });
                },
                () => {
                    /* silent on permission-denied / timeout */
                },
                { enableHighAccuracy: true, maximumAge: 4000, timeout: 4000 },
            );
        };

        push();
        const id = setInterval(push, intervalMs);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [enabled, intervalMs]);
}
